'use client'

import { useEffect, useRef, useState } from 'react'
import { RefreshIcon } from '@/components/icons'

const TRIGGER_RAW_DISTANCE = 180
const ACTIVATION_RAW_DISTANCE = 36
const PULL_DEAD_ZONE = 24
const MAX_DISTANCE = 64

type PullPhase = 'idle' | 'pulling' | 'ready' | 'refreshing'

export function isIosTouchDevice(userAgent: string, platform: string, maxTouchPoints: number): boolean {
  return /iPad|iPhone|iPod/i.test(userAgent)
    || (platform === 'MacIntel' && maxTouchPoints > 1)
}

export function resistedPullDistance(rawDistance: number): number {
  return Math.min(MAX_DISTANCE, Math.max(0, rawDistance - PULL_DEAD_ZONE) * 0.28)
}

function outermostScrollContainer(target: EventTarget | null, root: HTMLElement): HTMLElement | null {
  if (!(target instanceof HTMLElement) || !root.contains(target)) return null
  if (target.closest('input, textarea, select, [contenteditable="true"], [data-no-pull-refresh]')) return null

  let candidate: HTMLElement | null = null
  let current: HTMLElement | null = target
  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight + 1) {
      candidate = current
    }
    if (current === root) break
    current = current.parentElement
  }
  return candidate || root
}

export default function IosPullToRefresh({ onRefresh }: { onRefresh?: () => void }) {
  const [enabled, setEnabled] = useState(false)
  const [distance, setDistance] = useState(0)
  const [phase, setPhase] = useState<PullPhase>('idle')
  const phaseRef = useRef<PullPhase>('idle')
  const gesture = useRef<{
    startX: number
    startY: number
    scrollContainer: HTMLElement
  } | null>(null)
  const distanceRef = useRef(0)
  const rawDistanceRef = useRef(0)

  useEffect(() => {
    setEnabled(isIosTouchDevice(navigator.userAgent, navigator.platform, navigator.maxTouchPoints))
  }, [])

  useEffect(() => {
    if (!enabled) return
    const root = document.querySelector<HTMLElement>('[data-pull-refresh-root]')
    if (!root) return
    const previousDocumentOverscroll = document.documentElement.style.overscrollBehaviorY
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY
    document.documentElement.style.overscrollBehaviorY = 'none'
    document.body.style.overscrollBehaviorY = 'none'
    let settleTimer: number | undefined

    const moveContent = (nextDistance: number, animated: boolean) => {
      if (settleTimer !== undefined) window.clearTimeout(settleTimer)
      root.style.transition = animated ? 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none'
      root.style.transform = `translate3d(0, ${nextDistance}px, 0)`
      root.style.willChange = nextDistance > 0 ? 'transform' : ''
      if (animated && nextDistance === 0) {
        settleTimer = window.setTimeout(() => {
          root.style.transition = ''
          root.style.transform = ''
          root.style.willChange = ''
        }, 340)
      }
    }

    const reset = (animated = true) => {
      gesture.current = null
      distanceRef.current = 0
      rawDistanceRef.current = 0
      setDistance(0)
      phaseRef.current = 'idle'
      setPhase('idle')
      moveContent(0, animated)
    }
    const handleTouchStart = (event: TouchEvent) => {
      if (phaseRef.current === 'refreshing' || event.touches.length !== 1) return
      const scrollContainer = outermostScrollContainer(event.target, root)
      if (!scrollContainer || scrollContainer.scrollTop > 0) return
      gesture.current = {
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        scrollContainer,
      }
    }
    const handleTouchMove = (event: TouchEvent) => {
      const active = gesture.current
      if (!active || event.touches.length !== 1) return
      if (active.scrollContainer.scrollTop > 0) {
        reset()
        return
      }
      const deltaX = event.touches[0].clientX - active.startX
      const deltaY = event.touches[0].clientY - active.startY
      if (deltaY <= 0 || Math.abs(deltaX) > deltaY) {
        reset()
        return
      }
      event.preventDefault()
      if (deltaY < ACTIVATION_RAW_DISTANCE) return
      const nextDistance = resistedPullDistance(deltaY)
      rawDistanceRef.current = deltaY
      distanceRef.current = nextDistance
      setDistance(nextDistance)
      moveContent(nextDistance, false)
      phaseRef.current = deltaY >= TRIGGER_RAW_DISTANCE ? 'ready' : 'pulling'
      setPhase(phaseRef.current)
    }
    const handleTouchEnd = () => {
      if (!gesture.current) return
      const shouldRefresh = rawDistanceRef.current >= TRIGGER_RAW_DISTANCE
      gesture.current = null
      if (!shouldRefresh) {
        reset()
        return
      }
      setDistance(58)
      moveContent(48, true)
      phaseRef.current = 'refreshing'
      setPhase('refreshing')
      window.setTimeout(() => {
        if (onRefresh) onRefresh()
        else window.location.reload()
      }, 120)
    }
    const handleTouchCancel = () => reset()

    root.addEventListener('touchstart', handleTouchStart, { passive: true })
    root.addEventListener('touchmove', handleTouchMove, { passive: false })
    root.addEventListener('touchend', handleTouchEnd, { passive: true })
    root.addEventListener('touchcancel', handleTouchCancel, { passive: true })
    return () => {
      root.removeEventListener('touchstart', handleTouchStart)
      root.removeEventListener('touchmove', handleTouchMove)
      root.removeEventListener('touchend', handleTouchEnd)
      root.removeEventListener('touchcancel', handleTouchCancel)
      if (settleTimer !== undefined) window.clearTimeout(settleTimer)
      root.style.transition = ''
      root.style.transform = ''
      root.style.willChange = ''
      document.documentElement.style.overscrollBehaviorY = previousDocumentOverscroll
      document.body.style.overscrollBehaviorY = previousBodyOverscroll
    }
  }, [enabled, onRefresh])

  if (!enabled) return null
  const visible = phase !== 'idle'
  const showLabel = phase === 'ready' || phase === 'refreshing'
  const bubbleProgress = Math.min(1, distance / 28)
  const label = phase === 'refreshing'
    ? 'Обновляем…'
    : 'Отпустите для обновления'

  return (
    <div
      role="status"
      aria-label="Обновление страницы"
      aria-live="polite"
      className={`pointer-events-none fixed left-1/2 z-[90] flex flex-col items-center md:hidden ${
        phase === 'pulling' || phase === 'ready'
          ? 'transition-none'
          : 'transition-[opacity,transform] duration-180 ease-out'
      }`}
      style={{
        top: 'calc(env(safe-area-inset-top) + 54px)',
        opacity: visible ? 1 : 0,
        transform: `translate3d(-50%, ${visible ? distance - 48 : -64}px, 0)`,
        willChange: 'transform, opacity',
      }}
    >
      <span
        className="grid size-10 place-items-center rounded-full border border-rule-strong bg-paper text-gold-deep shadow-[0_8px_24px_rgba(31,27,22,0.16)] transition-[opacity,transform] duration-180 ease-out"
        style={{
          opacity: visible ? bubbleProgress : 0,
          transform: `scale(${0.72 + bubbleProgress * 0.28})`,
        }}
      >
        <span
          className={phase === 'refreshing' ? 'animate-spin' : undefined}
        >
          <RefreshIcon />
        </span>
      </span>
      <span
        className="mt-1.5 whitespace-nowrap rounded-full bg-onyx/88 px-2.5 py-1 text-[9px] font-medium text-ivory shadow-sm transition-[opacity,transform] duration-180 ease-out"
        style={{
          opacity: showLabel ? 1 : 0,
          transform: `translateY(${showLabel ? 0 : -4}px) scale(${showLabel ? 1 : 0.94})`,
        }}
      >
        {label}
      </span>
    </div>
  )
}
